import type { PredictorConfig } from "./config";
import type { Bean } from "./model";

/**
 * Per-attribute-value offsets in ln-time space: delta_ln = ln(t_bean / t_ref) at the same grind setting, where the
 * reference bean is medium roast, washed, standard density, caffeinated. Grinder-independent. Mirrors
 * AttributeOffsets.java. Seeds are folklore placeholders; "unknown" and decaf=false contribute 0 and are never learned.
 */
export const ROAST_LEVEL = "roast_level";
export const PROCESS = "process";
export const DENSITY_CLASS = "density_class";
export const IS_DECAF = "is_decaf";

export interface Offset {
  readonly attribute: string;
  readonly value: string;
  readonly deltaLn: number;
  readonly nBeans: number;
}

export interface AttributeValue {
  readonly attribute: string;
  readonly value: string;
}

export class AttributeOffsets {
  private constructor(private readonly byKey: ReadonlyMap<string, Offset>) {}

  static of(rows: readonly Offset[]): AttributeOffsets {
    const m = new Map<string, Offset>();
    for (const o of rows) m.set(key(o.attribute, o.value), o);
    return new AttributeOffsets(m);
  }

  /**
   * The seed table with n_beans = 0. Roast level, process and density all start at zero: the owner's experience is
   * that light roasts on their grinder start at the reference setting, and sources disagree on the other directions,
   * so every attribute is learned rather than guessed. Decaf keeps +0.15 (runs slower). Mirrors Java.
   */
  static seeds(): AttributeOffsets {
    const rows: Offset[] = [
      ...["light", "medium_light", "medium", "medium_dark", "dark"].map((r) => ({ attribute: ROAST_LEVEL, value: r, deltaLn: 0.0, nBeans: 0 })),
      ...["washed", "honey", "natural", "anaerobic"].map((p) => ({ attribute: PROCESS, value: p, deltaLn: 0.0, nBeans: 0 })),
      ...["high", "standard", "low"].map((d) => ({ attribute: DENSITY_CLASS, value: d, deltaLn: 0.0, nBeans: 0 })),
      { attribute: IS_DECAF, value: "true", deltaLn: 0.15, nBeans: 0 },
    ];
    return AttributeOffsets.of(rows);
  }

  /** True when no row has been learned from yet (every nBeans === 0). */
  isUnlearned(): boolean {
    return [...this.byKey.values()].every((o) => o.nBeans === 0);
  }

  /** Row-for-row equality of values and counts. */
  sameAs(other: AttributeOffsets): boolean {
    const a = this.entries(), b = other.entries();
    return a.length === b.length && a.every((o, i) => {
      const p = b[i] as Offset;
      return o.attribute === p.attribute && o.value === p.value && o.deltaLn === p.deltaLn && o.nBeans === p.nBeans;
    });
  }

  entries(): Offset[] {
    return [...this.byKey.values()];
  }

  /** The stored offset, or a zero row when the value is unknown to the table. */
  get(attribute: string, value: string): Offset {
    return this.byKey.get(key(attribute, value)) ?? { attribute, value, deltaLn: 0.0, nBeans: 0 };
  }

  withOffset(o: Offset): AttributeOffsets {
    const m = new Map(this.byKey);
    m.set(key(o.attribute, o.value), o);
    return new AttributeOffsets(m);
  }

  /** δ_bean = Σ delta_ln over the bean's attributes, clamped to ±cfg.deltaBeanClamp. */
  deltaBean(bean: Bean, cfg: PredictorConfig): number {
    let sum = 0;
    for (const av of attributesOf(bean)) sum += this.get(av.attribute, av.value).deltaLn;
    const c = cfg.deltaBeanClamp;
    return Math.max(-c, Math.min(c, sum));
  }
}

/** The bean's learnable attribute values: unknowns and decaf=false are excluded. Order matches Java. */
export function attributesOf(bean: Bean): AttributeValue[] {
  const out: AttributeValue[] = [];
  if (bean.roastLevel !== "unknown") out.push({ attribute: ROAST_LEVEL, value: bean.roastLevel });
  if (bean.processClass !== "unknown") out.push({ attribute: PROCESS, value: bean.processClass });
  if (bean.densityClass !== "unknown") out.push({ attribute: DENSITY_CLASS, value: bean.densityClass });
  if (bean.decaf) out.push({ attribute: IS_DECAF, value: "true" });
  return out;
}

/** Grind-unit conversion for a grinder with slope b: Δx = −δ / b (a faster-running bean starts finer). */
export function deltaX(deltaLn: number, slopeB: number): number {
  return -deltaLn / slopeB;
}

function key(attribute: string, value: string): string {
  return `${attribute}:${value}`;
}
